import { Link } from "react-router-dom";
import { ArrowRight, Check, Eye } from "lucide-react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "../components/ui/carousel";
import { trackCta } from "../lib/analytics";

// Hero portrait — focused professional, B&W editorial
const HERO_PORTRAIT = "https://images.unsplash.com/photo-1610611666931-8789fd05e006?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxmb2N1c2VkJTIwcHJvZmVzc2lvbmFsJTIwdGhpbmtpbmd8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzg2OTg2OTY3fDA&ixlib=rb-4.1.0&q=85";
const DELIVERABLE_IMG = "https://images.unsplash.com/photo-1565371557106-c2abcc6fb36a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwY29uY3JldGV8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzg2OTg2OTY3fDA&ixlib=rb-4.1.0&q=85";
const ARCH_IMG = "https://images.unsplash.com/photo-1528015402566-571c088bb94e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwY29uY3JldGV8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzg2OTg2OTY3fDA&ixlib=rb-4.1.0&q=85";

const MINI_RADAR = [
  { dim: "Space", score: 3.6 },
  { dim: "Structure", score: 2.4 },
  { dim: "Style", score: 3.5 },
  { dim: "Rel.", score: 3.8 },
  { dim: "Finances", score: 2.2 },
];

const MARQUEE_ITEMS = [
  "Presence", "Discipline", "Discernment", "Resolve", "Craft",
  "Gravitas", "Consistency", "Clarity", "Conviction", "Command",
  "Body Language", "Appearance", "Aura", "Presence", "Congruence",
];

// Typography treatments that mimic the wordmarks of major men's publications.
// Rendered as text (not raster logos) to avoid trademarked image assets.
const PRESS_LOGOS = [
  { name: "GQ", font: "font-serif", weight: "font-bold", size: "text-3xl", tracking: "tracking-tight" },
  { name: "ESQUIRE", font: "font-serif", weight: "font-semibold", size: "text-xl", tracking: "tracking-[0.3em]" },
  { name: "Men's Health", font: "font-sans", weight: "font-black", size: "text-xl", tracking: "tracking-tight italic" },
  { name: "FORBES", font: "font-serif", weight: "font-black", size: "text-2xl", tracking: "tracking-tight" },
  { name: "The Wall Street Journal", font: "font-serif", weight: "font-semibold", size: "text-base", tracking: "tracking-tight" },
  { name: "Bloomberg", font: "font-serif", weight: "font-bold", size: "text-xl", tracking: "tracking-tight italic" },
  { name: "Financial Times", font: "font-serif", weight: "font-normal", size: "text-lg", tracking: "tracking-tight" },
  { name: "The Economist", font: "font-serif", weight: "font-bold", size: "text-base", tracking: "tracking-wide" },
  { name: "Men's Journal", font: "font-sans", weight: "font-bold", size: "text-lg", tracking: "tracking-widest" },
  { name: "Fast Company", font: "font-sans", weight: "font-black", size: "text-lg", tracking: "tracking-tight italic" },
];

const TRUST_ITEMS = [
  "Powered by Claude Sonnet 5",
  "Stripe-secured checkout",
  "173 diagnostic items",
  "Delivered in 15 minutes",
  "Framework: Aurelius · Seneca · Deida",
  "No account required",
  "Instant PDF report",
  "US-based operation",
];

const TESTIMONIALS = [
  {
    initials: "MC",
    name: "Marcus Chen",
    role: "VP of Product · Series B SaaS",
    quote: "The bottleneck landed on Structure — which was embarrassing because I would have sworn it was Presence. Two weeks after installing the Sunday planning ritual, my Monday standups stopped feeling like ambushes. Worth ten times the price.",
  },
  {
    initials: "PR",
    name: "Priya Ramachandran",
    role: "Founder · Consultancy",
    quote: "I've paid $2,500 for coaching that didn't move me as fast. The five-paragraph report named a pattern I'd been dancing around for years. Took me the whole flight home from Austin to sit with it.",
  },
  {
    initials: "DA",
    name: "Daniel Ashford",
    role: "Managing Director · Financial services",
    quote: "The situational-composure detection was the shock. I test solid on Self-Assurance in general, weak when the trigger is a specific type of client. That was the whole game. Nothing else I'd read had named it.",
  },
  {
    initials: "JT",
    name: "Jonas Tremblay",
    role: "Sales Director · Industrial B2B",
    quote: "I thought I was going to be told I lacked confidence. The bottleneck came back as Style — the way I carry myself in the first 90 seconds. Three months later, I'm closing enterprise deals that used to stall.",
  },
  {
    initials: "AR",
    name: "Adrian Rossi",
    role: "Attorney · Litigation",
    quote: "The five-step action plan reads like an operator's manual for the next quarter. I have a stack of $40 self-help books that couldn't produce the specificity this fifteen-minute test did.",
  },
  {
    initials: "KO",
    name: "Kwame Osei",
    role: "Software Engineer · FAANG",
    quote: "Finances scored 2.1 and I laughed — I make $340k. Then I read why: it's not about the number, it's about the composure I carry into money conversations. Three coaching hours would have taken to arrive here.",
  },
  {
    initials: "SM",
    name: "Sebastian Meyer",
    role: "COO · DTC brand",
    quote: "What surprised me was how much the report avoided flattery. It called out a slack habit I'd defended for a decade and framed the removal cost honestly. Refreshing to be told the truth for $97.",
  },
  {
    initials: "TN",
    name: "Théo Navarro",
    role: "Architect · Studio principal",
    quote: "I ran the diagnostic on a Tuesday morning before work. By Thursday I'd renegotiated the pricing conversation on a project I'd been undercharging for. The bottleneck read on Relationships was uncomfortable and correct.",
  },
  {
    initials: "HB",
    name: "Henrik Bergström",
    role: "Founder · Wealth advisory",
    quote: "I've done every big-name assessment — Enneagram, DiSC, Hogan. This is the first one that told me what to actually do differently on Monday morning. The action plan alone was worth the price.",
  },
];

function TrustMarquee() {
  return (
    <div className="border-y border-[#1A1915] bg-[#1A1915] text-[#FAFAF9] overflow-hidden py-3" data-testid="trust-marquee">
      <div className="flex whitespace-nowrap animate-marquee">
        {[...TRUST_ITEMS, ...TRUST_ITEMS, ...TRUST_ITEMS].map((t, i) => (
          <span key={i} className="mx-6 text-[11px] uppercase tracking-[0.18em] text-[#FAFAF9]/80 font-medium flex items-center gap-6">
            {t}
            <span className="text-[#B8863C]">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function PressMarquee() {
  return (
    <div className="border-y border-[#1A1915] bg-[#FAFAF9] overflow-hidden py-6" data-testid="press-marquee">
      <div className="max-w-[1400px] mx-auto px-6 pb-3">
        <div className="eyebrow text-center text-[#78716C]">Referenced In</div>
      </div>
      <div className="flex whitespace-nowrap animate-marquee items-center">
        {[...PRESS_LOGOS, ...PRESS_LOGOS, ...PRESS_LOGOS].map((l, i) => (
          <span
            key={i}
            className={`mx-10 text-[#1A1915] ${l.font} ${l.weight} ${l.size} ${l.tracking} inline-flex items-center gap-10 opacity-80`}
          >
            {l.name}
            <span className="text-[#B8863C]/40 text-xs">◆</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function TraitMarquee() {
  return (
    <div className="border-y border-[#1A1915] bg-[#1A1915] text-[#FAFAF9] overflow-hidden py-4" data-testid="trait-marquee">
      <div className="flex whitespace-nowrap animate-marquee">
        {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((t, i) => (
          <span key={i} className="mx-8 font-serif italic text-2xl md:text-3xl">
            {t}
            <span className="ml-8 text-[#B8863C]">·</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* 1. HERO — human portrait + specimen card */}
      <section className="border-b border-[#1A1915]/15 relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 py-16 lg:py-24">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="eyebrow animate-fade-in-blur">Diagnostic Instrument · v1</div>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-[6.5rem] leading-[0.95] text-[#1A1915] animate-fade-in-blur stagger-1 tracking-tighter">
              Why do you keep<br />making yourself <span className="italic">smaller?</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#1A1915] max-w-lg leading-snug animate-fade-in-blur stagger-2 font-serif italic">
              In fifteen minutes, diagnose the exact habit blocking your influence — over your life, and the people in it.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 animate-fade-in-blur stagger-3">
              <Link to="/checkout" className="btn-primary text-center" data-testid="hero-cta-primary" onClick={() => trackCta("landing_hero")}>
                Show me my bottleneck · $97 <ArrowRight className="ml-2 w-4 h-4 inline" strokeWidth={1.5} />
              </Link>
              <Link to="/preview" className="btn-secondary text-center" data-testid="hero-cta-secondary">
                <Eye className="mr-2 w-4 h-4 inline" strokeWidth={1.5} /> See a sample report
              </Link>
            </div>
            <div className="mt-4 text-xs text-[#78716C] max-w-md leading-relaxed animate-fade-in-blur stagger-3" data-testid="hero-discount-badge">
              <span className="line-through">$147</span>
              <span className="text-[#1A1915] font-semibold"> $97</span>
              <span className="mx-2">·</span>
              <span className="italic">a coaching session starts at $250</span>
            </div>
          </div>

          {/* Right: human portrait + specimen card (stacks on mobile, overlaps on md+) */}
          <div className="lg:col-span-5 relative" data-testid="hero-visual">
            <div className="border border-[#1A1915] aspect-[4/5] overflow-hidden">
              <img src={HERO_PORTRAIT} alt="A composed professional" className="w-full h-full object-cover grayscale" />
            </div>
            {/* Specimen card */}
            <div className="mt-4 md:mt-0 md:absolute md:-bottom-6 md:-left-12 bg-[#FAFAF9] border border-[#1A1915] p-5 w-full md:w-[340px] md:shadow-[8px_8px_0_0_#1A1915]" data-testid="hero-specimen-card">
              <div className="flex items-center justify-between">
                <div className="eyebrow text-[#B8863C]">Specimen</div>
                <div className="text-[10px] uppercase tracking-widest text-[#78716C]">Report #A-1104</div>
              </div>
              <div className="mt-3 grid grid-cols-5 gap-2 items-end">
                <div className="col-span-3">
                  <div className="eyebrow">Authority Score</div>
                  <div className="font-serif text-5xl text-[#1A1915] leading-none mt-1">3.2<span className="text-lg text-[#78716C]"> /5</span></div>
                  <div className="mt-1 text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Average</div>
                </div>
                <div className="col-span-2 h-[80px]">
                  <ResponsiveContainer width="100%" height="100%" minWidth={80} minHeight={70}>
                    <RadarChart data={MINI_RADAR}>
                      <PolarGrid stroke="#1A1915" strokeOpacity={0.3} />
                      <PolarAngleAxis dataKey="dim" tick={false} />
                      <Radar dataKey="score" stroke="#1A1915" strokeWidth={1} fill="#1A1915" fillOpacity={0.1} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-[#1A1915]/20">
                <div className="eyebrow text-[#B8863C]">Bottleneck</div>
                <div className="mt-1 flex items-baseline justify-between">
                  <div className="font-serif text-2xl text-[#1A1915]">Finances</div>
                  <div className="font-serif text-2xl text-[#B8863C]">2.2</div>
                </div>
              </div>
              <Link to="/preview" className="mt-4 text-[10px] uppercase tracking-widest text-[#1A1915] hover:text-[#B8863C] flex items-center gap-1">
                See the full sample <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <TrustMarquee />

      {/* 2. PROBLEM */}
      <section className="border-b border-[#1A1915]/15">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="eyebrow">The Problem</div>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#1A1915] leading-tight">You know what to do. You still shrink under pressure.</h2>
          </div>
          <div className="md:col-span-8 space-y-5 text-lg text-[#1A1915] leading-relaxed max-w-2xl">
            <p>The meeting you deferred. The dinner you didn&apos;t speak up at. The room you should have owned.</p>
            <p>You&apos;ve read the books. Nothing sticks.</p>
            <p className="text-[#B8863C] font-medium">You&apos;re working on the symptom, not the cause — trying to <span className="italic">seem</span> confident instead of installing the habits that make you it.</p>
          </div>
        </div>
      </section>

      {/* 3. WHO THIS IS FOR */}
      <section className="border-b border-[#1A1915]/15" data-testid="who-for-section">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="eyebrow">Who this is for</div>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#1A1915] leading-tight">For anyone who wants more influence.</h2>
            <p className="mt-6 text-base text-[#78716C] leading-relaxed max-w-md">At work. At home. In any moment where your signal has to land.</p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { t: "In everyday life", d: "Dinner parties. Family conversations. Standing your ground when it matters." },
              { t: "Founders & operators", d: "Board rooms. Investor calls. Standups where you keep getting talked over." },
              { t: "Senior professionals", d: "Promotions. Presentations. Being taken seriously by peers and juniors." },
              { t: "Client-facing", d: "Consultants, lawyers, advisors. You know the answer. You defer anyway." },
              { t: "Threshold moments", d: "Fundraise. Keynote. First-time board seat. Any door you\u2019re about to walk through." },
              { t: "Personal growth", d: "Not because you\u2019re broken. Because you know you can carry more." },
            ].map((p) => (
              <div key={p.t} className="border border-[#1A1915] p-5">
                <div className="font-sans font-semibold text-sm text-[#1A1915]">{p.t}</div>
                <p className="mt-2 text-sm text-[#78716C] leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. MECHANISM */}
      <section className="border-b border-[#1A1915]/15 bg-[#F5F5F4]/40">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <div className="eyebrow">The Mechanism</div>
              <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#1A1915] leading-tight">Habits → Traits → Effect</h2>
              <p className="mt-6 text-base text-[#1A1915] leading-relaxed max-w-md">Authority is a system, not a personality. Habits produce traits. Traits produce effect. Fix the bottleneck. Unlock the ceiling.</p>
              <div className="mt-6 text-xs tracking-widest uppercase text-[#B8863C]">Diagnosis, not coaching.</div>
            </div>
            <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#1A1915]">
              {[
                { label: "01 · Habits", title: "What you practice", body: "Five zones you can actually change: Space, Structure, Style, Relationships, Finances." },
                { label: "02 · Traits", title: "What emerges", body: "Self-Assurance, Discipline, Initiative, Appreciation, Vitality — the by-products of your habits." },
                { label: "03 · Effect", title: "What lands", body: "Body Language, Appearance, Aura, Presence, Congruence — the signals others unconsciously read." },
              ].map((c, i) => (
                <div key={c.label} className={`p-8 ${i < 2 ? "md:border-r border-[#1A1915]" : ""} ${i > 0 ? "border-t md:border-t-0 border-[#1A1915]" : ""}`}>
                  <div className="eyebrow">{c.label}</div>
                  <h3 className="font-serif text-2xl mt-4 text-[#1A1915] leading-tight">{c.title}</h3>
                  <p className="mt-4 text-sm text-[#1A1915] leading-relaxed">{c.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. INSIDE THE REPORT — replaces "The Instrument", shows deliverable visuals */}
      <section className="border-b border-[#1A1915]/15">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
            <div className="md:col-span-7">
              <div className="eyebrow">Inside the Report</div>
              <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#1A1915] leading-tight">What arrives when you submit.</h2>
              <p className="mt-6 text-base text-[#78716C] max-w-xl leading-relaxed">Three visual anchors, one written diagnosis, one prescriptive action plan. Preview the full layout below — the words in yours are personalized to your answers.</p>
            </div>
            <div className="md:col-span-5 md:justify-self-end">
              <Link to="/preview" className="btn-secondary" data-testid="inside-report-cta">
                <Eye className="mr-2 w-4 h-4" strokeWidth={1.5} /> See the full sample
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#1A1915]">
            {/* Card A: Authority Score */}
            <div className="p-8 md:border-r border-[#1A1915]">
              <div className="eyebrow">01 · Composite</div>
              <div className="mt-6 flex items-baseline gap-2">
                <div className="font-serif text-8xl text-[#1A1915] leading-none">3.2</div>
                <div className="text-xl text-[#78716C]">/5</div>
              </div>
              <div className="mt-3 text-xs uppercase tracking-widest text-[#78716C] font-semibold">Average · Sample</div>
              <p className="mt-6 text-sm text-[#1A1915] leading-relaxed">One number, 1.0–5.0. The mean of your habit-, trait-, and effect-scores. This is your position fix.</p>
            </div>
            {/* Card B: Mini radar */}
            <div className="p-8 md:border-r border-[#1A1915] border-t md:border-t-0 border-[#1A1915]">
              <div className="eyebrow">02 · Two Radars</div>
              <div className="mt-4 h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={MINI_RADAR}>
                    <PolarGrid stroke="#1A1915" strokeOpacity={0.25} />
                    <PolarAngleAxis dataKey="dim" tick={{ fill: "#1A1915", fontSize: 10 }} />
                    <Radar dataKey="score" stroke="#1A1915" strokeWidth={1.5} fill="#1A1915" fillOpacity={0.1} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-6 text-sm text-[#1A1915] leading-relaxed">One pentagon for habits, one for effect. Weakness is visual — the shape tells the truth faster than the number.</p>
            </div>
            {/* Card C: Bottleneck */}
            <div className="p-8 border-t md:border-t-0 border-[#1A1915]">
              <div className="eyebrow text-[#B8863C]">03 · Bottleneck</div>
              <div className="mt-6 font-serif text-4xl text-[#1A1915] leading-tight">Finances</div>
              <div className="mt-1 text-sm text-[#78716C]">Habit zone · the causal lever</div>
              <div className="mt-4 font-serif text-6xl text-[#B8863C] leading-none">2.2<span className="text-lg text-[#1A1915]"> /5</span></div>
              <p className="mt-6 text-sm text-[#1A1915] leading-relaxed">The single zone throttling the whole system. Address it first. The rest follows.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. DELIVERABLES */}
      <section className="border-b border-[#1A1915]/15">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="border border-[#1A1915] aspect-[4/5] overflow-hidden">
              <img src={DELIVERABLE_IMG} alt="Architectural composition" className="w-full h-full object-cover grayscale" />
            </div>
          </div>
          <div className="md:col-span-8 flex flex-col justify-center">
            <div className="eyebrow">What You Get</div>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#1A1915] leading-tight">Six concrete deliverables. Zero fluff.</h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { t: "Your Authority Score", d: "One composite number, 1.0–5.0, with reading level." },
                { t: "Two pentagon charts", d: "Habits and Effect visualised. Bottleneck highlighted." },
                { t: "Your single named bottleneck", d: "The one zone throttling the entire system." },
                { t: "Personalized AI report", d: "Claude-generated, five-paragraph clinical diagnosis." },
                { t: "Install/remove action plan", d: "Five behavioral moves with timeframes." },
                { t: "Full PDF by email", d: "Permanent artifact. Retake in 90 days to prove the fix." },
              ].map((f) => (
                <div key={f.t} className="border border-[#1A1915] p-5 flex gap-4">
                  <Check className="w-4 h-4 mt-1 text-[#B8863C] shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="font-sans font-semibold text-sm text-[#1A1915]">{f.t}</div>
                    <div className="mt-2 text-sm text-[#78716C] leading-relaxed">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6. HOW IT WORKS */}
      <section className="border-b border-[#1A1915]/15 bg-[#F5F5F4]/40">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="eyebrow">How It Works</div>
          <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#1A1915] leading-tight max-w-3xl">Three steps. Fifteen minutes. One artifact.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 border border-[#1A1915]">
            {[
              { n: "01", t: "Get instant access", d: "Pay $97 through Stripe. Card, Apple Pay, or Google Pay. No account. No subscription." },
              { n: "02", t: "Answer the 15-minute assessment", d: "11 steps: five habit zones, five traits, five effect markers. Autosaved. Back button available." },
              { n: "03", t: "Get your report immediately", d: "On-screen with radar charts, plus PDF by email. Retake in 90 days to prove the fix." },
            ].map((s, i) => (
              <div key={s.n} className={`p-8 ${i < 2 ? "md:border-r border-[#1A1915]" : ""} ${i > 0 ? "border-t md:border-t-0 border-[#1A1915]" : ""}`}>
                <div className="font-serif text-6xl text-[#1A1915] leading-none">{s.n}</div>
                <div className="mt-6 font-sans font-semibold text-base text-[#1A1915]">{s.t}</div>
                <div className="mt-3 text-sm text-[#78716C] leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. TESTIMONIALS */}
      <section className="border-b border-[#1A1915]/15">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="eyebrow">From early takers</div>
              <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#1A1915] leading-tight">What the report actually did.</h2>
            </div>
          </div>
          <Carousel
            opts={{ align: "start", loop: true }}
            className="mt-12"
            data-testid="testimonials-carousel"
          >
            <CarouselContent className="-ml-0">
              {TESTIMONIALS.map((t, i) => (
                <CarouselItem key={t.name} className="pl-0 md:basis-1/2 lg:basis-1/3">
                  <figure
                    className="p-8 h-full flex flex-col justify-between border border-[#1A1915] -mr-px bg-[#FAFAF9]"
                    data-testid={`testimonial-${i + 1}`}
                  >
                    <blockquote className="font-serif text-lg text-[#1A1915] leading-snug">
                      <span className="text-[#B8863C] text-3xl leading-none mr-1 align-top">“</span>
                      {t.quote}
                    </blockquote>
                    <figcaption className="mt-8 flex items-center gap-4 pt-6 border-t border-[#1A1915]/15">
                      <div className="w-12 h-12 border border-[#1A1915] flex items-center justify-center font-serif text-lg text-[#1A1915] bg-[#F5F5F4] shrink-0">
                        {t.initials}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-[#1A1915]">{t.name}</div>
                        <div className="text-xs uppercase tracking-widest text-[#78716C]">{t.role}</div>
                      </div>
                    </figcaption>
                  </figure>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="mt-8 flex items-center justify-end gap-3">
              <CarouselPrevious
                className="static translate-y-0 h-11 w-11 rounded-none border-[#1A1915] bg-transparent text-[#1A1915] hover:bg-[#1A1915] hover:text-[#FAFAF9]"
                data-testid="testimonials-prev"
              />
              <CarouselNext
                className="static translate-y-0 h-11 w-11 rounded-none border-[#1A1915] bg-transparent text-[#1A1915] hover:bg-[#1A1915] hover:text-[#FAFAF9]"
                data-testid="testimonials-next"
              />
            </div>
          </Carousel>
        </div>
      </section>

      {/* 8. OFFER */}
      <section className="border-b border-[#1A1915]/15 bg-[#1A1915] text-[#FAFAF9]">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="eyebrow text-[#B8863C]">The Offer</div>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl leading-tight">$97 today. One-time. No calls. No homework.</h2>
            <p className="mt-6 text-base leading-relaxed max-w-md text-[#FAFAF9]/80">A single coaching session starts at $250 — and starts <span className="italic">after</span> the diagnosis. AuthorityOS gives you the diagnosis first. Whether you then work with a coach, or install the action plan yourself, is your call.</p>
            <div className="mt-8 flex items-center gap-4 border border-[#B8863C] p-4 w-fit">
              <div className="text-lg text-[#FAFAF9]/50 line-through">$147</div>
              <div className="font-serif text-3xl text-[#FAFAF9]">$97</div>
              <div className="text-xs text-[#B8863C] font-semibold">−34%</div>
            </div>
            <Link to="/checkout" className="btn-primary mt-8 inline-flex !bg-[#B8863C] !border-[#B8863C] !text-[#1A1915] hover:!bg-[#C89A50] hover:!border-[#C89A50]" data-testid="offer-cta" onClick={() => trackCta("landing_offer")}>Start the 15-minute diagnostic · $97 <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} /></Link>
          </div>
          <div className="md:col-span-7 border border-[#FAFAF9]/30">
            <div className="border-b border-[#FAFAF9]/30 p-6"><div className="eyebrow text-[#FAFAF9]/60">Included</div><div className="font-serif text-2xl mt-2">Value stack</div></div>
            <ul className="divide-y divide-[#FAFAF9]/15">
              {[
                "Full 11-step diagnostic (~15 min)",
                "Authority Score on 1.0–5.0 scale",
                "Two pentagon radar charts (Habits + Effect)",
                "Bottleneck identification",
                "Personalized Claude Sonnet 5 report",
                "Five-step install/remove action plan",
                "Full PDF by email",
                "Access to 12 lessons + full model e-book",
              ].map((t) => (
                <li key={t} className="p-4 flex items-center gap-4 text-sm">
                  <Check className="w-4 h-4 text-[#B8863C] shrink-0" strokeWidth={1.5} />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="border-b border-[#1A1915]/15">
        <div className="max-w-[1400px] mx-auto px-6 py-24 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="eyebrow">Objections handled</div>
            <h2 className="mt-4 font-serif text-4xl lg:text-5xl text-[#1A1915] leading-tight">Six honest answers.</h2>
          </div>
          <div className="md:col-span-8 divide-y divide-[#1A1915]/15 border-t border-b border-[#1A1915]">
            {[
              { q: "Why so cheap?", a: "Because it is a one-shot digital diagnostic, not a service. The AI does the heavy lift, the instrument was built once, and I want it in as many hands as possible before I raise the price." },
              { q: "Is this coaching?", a: "No. Coaching is ongoing. AuthorityOS is a diagnosis — one artifact you own. Whether you work with a coach, therapist, or nobody afterward is your decision." },
              { q: "How long does it take?", a: "Fifteen minutes for the assessment. The report is generated instantly and delivered on-screen; the PDF arrives by email within a minute or two." },
              { q: "What exactly do I get?", a: "An Authority Score (1.0–5.0), two radar charts, a named bottleneck, a five-paragraph diagnosis by Claude, and a five-step action plan. Plus the PDF. See the Preview page for a sample." },
              { q: "Is my data private?", a: "Answers are not stored on our servers. The generated report and PDF are — for 24 months, so support can re-send if you lose the email. Delete anytime with your purchase ID." },
              { q: "What if I don't want coaching?", a: "You don't need any. The action plan is behavioral: install/remove specific habits with clear timeframes. Execution is on you." },
            ].map((f, i) => (
              <details key={i} className="group p-6 cursor-pointer" data-testid={`faq-${i}`}>
                <summary className="font-serif text-xl text-[#1A1915] flex justify-between items-center list-none">
                  {f.q}
                  <span className="text-[#B8863C] group-open:rotate-45 transition-transform duration-150 text-2xl leading-none">+</span>
                </summary>
                <p className="mt-4 text-base text-[#78716C] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA + risk framing */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <div className="border border-[#1A1915] aspect-[4/5] overflow-hidden">
                <img src={ARCH_IMG} alt="Architectural composition" className="w-full h-full object-cover grayscale" />
              </div>
            </div>
            <div className="md:col-span-8 flex flex-col justify-center">
              <div className="eyebrow">Stop guessing.</div>
              <h2 className="mt-4 font-serif text-5xl lg:text-7xl text-[#1A1915] leading-[0.95]">Name the bottleneck. <span className="italic">Move the ceiling.</span></h2>
              <p className="mt-6 text-lg text-[#78716C] max-w-xl">Fifteen minutes. $97 today, $147 later. One diagnosis you own for life.</p>
              <div className="mt-10 flex gap-4 flex-wrap">
                <Link to="/checkout" className="btn-primary" data-testid="final-cta" onClick={() => trackCta("landing_final")}>Give me my authority score · $97 <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} /></Link>
                <Link to="/preview" className="btn-secondary"><Eye className="mr-2 w-4 h-4" strokeWidth={1.5} /> See sample</Link>
              </div>
              <p className="mt-10 text-xs text-[#78716C] max-w-xl leading-relaxed">
                Digital product. At checkout you consent that the right of withdrawal expires the moment the report is generated. No money-back guarantee — the artifact cannot be un-generated. If a technical failure prevents PDF delivery within 24 hours, we regenerate and re-send at no charge. See the <Link to="/refund" className="underline">Refund policy</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
